import CheckIcon from '@mui/icons-material/Check';
import { useState, useEffect, useContext } from 'react';
import scrollToTop from '@/utils/scroolToTop';
import {
  BodyLarge,
  BodyLargeEmphasized,
  BodyMedium,
  NavBarBackButton,
  SearchBox,
} from '@/design-system';
import colors from '@/theme/colors';
import styles from './Language.styles';
import languagesJson from './languages.json';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import { AccessibleDivButton, LiveFeedback, PeachjarLoader } from '@/components';
import { useLiveFeedback } from '@/hooks';

export const Language: React.FC = () => {
  const {
    language: selectedLanguage,
    updateUserPreferences,
    loading,
  } = useContext(UserPreferencesContext);
  const [languageSearch, setLanguageSearch] = useState('');
  const [languages, setLanguages] = useState(Object.keys(languagesJson).map(language => language));
  const [feedbackMessage, setFeedbackMessage] = useLiveFeedback({ delay: 500 });

  useEffect(() => {
    const timer = setTimeout(() => {
      const filteredLanguages = Object.keys(languagesJson).filter(language =>
        language.toLowerCase().includes(languageSearch.toLowerCase())
      );
      setLanguages(filteredLanguages);
      setFeedbackMessage(`${filteredLanguages.length} languages found`);
    }, 500);

    return () => clearTimeout(timer);
  }, [languageSearch, setFeedbackMessage]);

  const onLanguageSearchChange = (value: string) => {
    setLanguageSearch(value);
  };

  const onSelectLanguage = async (newLanguage: string) => {
    const code = languagesJson[newLanguage];
    const language = newLanguage;
    await updateUserPreferences({
      language: {
        code,
        name: language,
      },
    });
    setLanguageSearch('');
    scrollToTop();
  };

  return (
    <>
      <NavBarBackButton trackId="languagePage">Language</NavBarBackButton>
      <PeachjarLoader loading={loading} />
      <div className={styles.container}>
        <div>
          <BodyMedium color={colors.slate}>
            Select the languages you prefer to view app content and receive email notifications in.
          </BodyMedium>
        </div>
        <LiveFeedback message={feedbackMessage} />
        <div className={styles.languageListContainer}>
          <SearchBox
            placeholder="Search for language"
            value={languageSearch}
            onChange={onLanguageSearchChange}
            showCancelButton
          />
          <div className={styles.languagesContainer}>
            {languageSearch.length === 0 && (
              <AccessibleDivButton
                className={styles.selectedLanguageContainer}
                aria-pressed="true"
                role="button"
                tabIndex={0}
              >
                <span className={styles.selectedLanguage}>
                  <BodyLargeEmphasized
                    data-testid="languageLabel"
                    nonTranslate
                    aria-label={`${selectedLanguage.name} is selected`}
                  >
                    {selectedLanguage.name}
                  </BodyLargeEmphasized>
                </span>
                <CheckIcon className={styles.checkIcon} aria-hidden="true" />
              </AccessibleDivButton>
            )}

            {languages.map(language => {
              if (language === selectedLanguage.name) return null;
              return (
                <AccessibleDivButton
                  className={styles.languageItem}
                  key={language}
                  onClick={async () => await onSelectLanguage(language)}
                  aria-label={`Select ${language} language`}
                >
                  <div className={styles.language}>
                    <BodyLarge nonTranslate data-testid="languageLabel">
                      {language}
                    </BodyLarge>
                  </div>
                </AccessibleDivButton>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};
